import { FiRefreshCw } from 'react-icons/fi';
import { useId, type FormEvent, type KeyboardEventHandler, type ReactNode } from 'react';
import './AnalyticsFilterBar.css';

interface AnalyticsFilterBarProps {
  children: ReactNode;
  className?: string;
}

interface FilterFieldProps {
  label: string;
  children: ReactNode;
  className?: string;
}

interface PeriodPresetsProps {
  active: (days: number) => boolean;
  onSelect: (days: number) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  className?: string;
}

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  placeholder?: string;
  maxLength?: number;
}

interface DateRangeProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onApply: (event: FormEvent<HTMLFormElement>) => void;
  error?: string | null;
  startLabel?: string;
  endLabel?: string;
  startMax?: string;
  endMin?: string;
  endMax?: string;
}

export function AnalyticsFilterBar({ children, className = '' }: AnalyticsFilterBarProps) {
  return <div className={`analytics-filter-bar ${className}`.trim()}>{children}</div>;
}

export function AnalyticsFilterGroup({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`analytics-filter-group ${className}`.trim()}>{children}</div>;
}

export function AnalyticsPeriodGroup({ presets, children }: { presets: ReactNode; children: ReactNode }) {
  return (
    <AnalyticsFilterGroup className="analytics-filter-period">
      <AnalyticsFilterSection label="Period">{presets}</AnalyticsFilterSection>
      {children}
    </AnalyticsFilterGroup>
  );
}

export function AnalyticsFilterField({ label, children, className = '' }: FilterFieldProps) {
  return (
    <label className={`analytics-filter-field ${className}`.trim()}>
      <span className="analytics-filter-label">{label}</span>
      {children}
    </label>
  );
}

export function AnalyticsFilterSection({ label, children, className = '' }: FilterFieldProps) {
  return (
    <div className={`analytics-filter-field ${className}`.trim()}>
      <span className="analytics-filter-label">{label}</span>
      {children}
    </div>
  );
}

export function AnalyticsPeriodPresets({ active, onSelect }: PeriodPresetsProps) {
  return (
    <div className="analytics-filter-presets" role="group" aria-label="Reporting period">
      {[1, 7, 30, 90].map((days) => (
        <button
          type="button"
          key={days}
          aria-pressed={active(days)}
          className={active(days) ? 'active' : ''}
          onClick={() => onSelect(days)}
        >
          {days === 1 ? 'Today' : `${days} days`}
        </button>
      ))}
    </div>
  );
}

export function AnalyticsSelectField({ label, value, options, onChange, className }: SelectFieldProps) {
  return (
    <AnalyticsFilterField label={label} className={className}>
      <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </AnalyticsFilterField>
  );
}

export function AnalyticsTextField({
  label,
  value,
  onChange,
  onKeyDown,
  placeholder,
  maxLength,
}: TextFieldProps) {
  return (
    <AnalyticsFilterField label={label}>
      <input
        value={value}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
      />
    </AnalyticsFilterField>
  );
}

export function AnalyticsDateRange({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApply,
  error,
  startLabel = 'From',
  endLabel = 'To',
  startMax,
  endMin,
  endMax,
}: DateRangeProps) {
  const errorId = useId();
  return (
    <form className="analytics-date-range" aria-label="Reporting dates" onSubmit={onApply}>
      <label>
        {startLabel}
        <input
          type="date"
          required
          value={startDate}
          max={startMax}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => onStartDateChange(event.target.value)}
        />
      </label>
      <label>
        {endLabel}
        <input
          type="date"
          required
          value={endDate}
          min={endMin}
          max={endMax}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => onEndDateChange(event.target.value)}
        />
      </label>
      <button type="submit">Apply</button>
      {error ? (
        <span id={errorId} className="analytics-date-error" role="alert">
          {error}
        </span>
      ) : null}
    </form>
  );
}

export function AnalyticsAdvancedFilters({ children }: { children: ReactNode }) {
  return (
    <details className="analytics-advanced-filters">
      <summary>Advanced filters</summary>
      <div className="analytics-advanced-fields">{children}</div>
    </details>
  );
}

export function AnalyticsFilterActions({ children }: { children: ReactNode }) {
  return <div className="analytics-filter-actions">{children}</div>;
}

export function AnalyticsRefreshButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="analytics-refresh" aria-label="Refresh report" onClick={onClick}>
      <FiRefreshCw aria-hidden="true" />
    </button>
  );
}

export function AnalyticsSecondaryButton({
  children,
  onClick,
  className = '',
}: {
  children: ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button type="button" className={`analytics-secondary ${className}`.trim()} onClick={onClick}>
      {children}
    </button>
  );
}

export function AnalyticsPrimaryButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" className="analytics-primary" onClick={onClick}>
      {children}
    </button>
  );
}
